/**
 * TemplatePreviewFrame — shows a page the SERVER composed (previewPortalExperiencePage)
 * at desktop, tablet or phone width.
 *
 * The HTML is the portal composer's output, never the author's raw markup, and it is
 * rendered in a sandboxed frame with every permission withheld: no scripts, no forms,
 * no same-origin access, no popups, no top navigation. The frame scrolls inside its own
 * box so a wide device never makes the page itself scroll sideways.
 * A device wider than the box starts at the box's left edge (auto margins, not flex
 * centring), so every part of it can be scrolled into view.
 *
 * States are distinct: loading (sized skeleton), error (message + retry), empty
 * (nothing to preview yet) and stale (the last good page stays, with a note).
 */
import React from "react";
import { Banner, Button, DevicePreviewToggle, InlineEmptyState, Skeleton, Spinner, resolveDeviceWidths, type PreviewDevice } from "@v0y4ge/jrni-ui";
import { t as i18nT } from "../../../lib/i18n";

export interface TemplatePreviewFrameProps {
  /** Server-composed HTML, or null when nothing has been previewed yet. */
  html: string | null;
  loading: boolean;
  error: string | null;
  /** Why the shown page is not current (e.g. "Fix the highlighted errors to update the preview"). */
  staleNote?: string | null;
  emptyTitle: string;
  emptyDescription?: string;
  onRetry?: () => void;
  /** Accessible name of the frame. */
  title: string;
  /** Height of the frame area. */
  height?: number | string;
}

const FRAME_HEIGHT = 560;

export function TemplatePreviewFrame(props: TemplatePreviewFrameProps) {
  const [device, setDevice] = React.useState<PreviewDevice>("desktop");
  const widths = resolveDeviceWidths();
  const height = props.height ?? FRAME_HEIGHT;
  const hasPage = typeof props.html === "string";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
        <DevicePreviewToggle value={device} onChange={setDevice} size="sm" ariaLabel={i18nT("journeys.customTemplates.previewWidth", "Preview width")} />
        {props.loading && hasPage ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "var(--jrni-color-text-muted)" }}>
            <Spinner size="sm" label={i18nT("journeys.customTemplates.updatingPreview", "Updating preview")} />
            {i18nT("journeys.customTemplates.updatingPreview", "Updating preview")}
          </span>
        ) : null}
      </div>
      {hasPage ? (
        <span style={{ fontSize: "12px", color: "var(--jrni-color-text-muted)" }}>
          {i18nT("journeys.customTemplates.previewOnlyHint", "Preview only. Open the published page to try booking.")}
        </span>
      ) : null}
      {props.error ? (
        <Banner tone="danger" title={i18nT("journeys.customTemplates.previewFailed", "The preview couldn't be shown")}
          action={props.onRetry ? <Button size="sm" hierarchy="secondary" onClick={props.onRetry}>{i18nT("journeys.customTemplates.retry", "Try again")}</Button> : undefined}>
          {props.error}
        </Banner>
      ) : null}
      {props.staleNote && hasPage ? (
        <Banner tone="warning" size="compact">{props.staleNote}</Banner>
      ) : null}
      <div
        style={{
          height, minWidth: 0, overflow: "auto", borderRadius: "var(--jrni-radius-md)",
          border: "1px solid var(--jrni-color-surface-border)", background: "var(--jrni-color-surface-sunken, var(--jrni-color-surface-2))",
          display: "flex", justifyContent: "flex-start", alignItems: "flex-start", padding: "12px",
        }}
      >
        {hasPage ? (
          <iframe
            title={props.title}
            sandbox=""
            referrerPolicy="no-referrer"
            srcDoc={props.html as string}
            style={{
              width: widths[device] + "px", maxWidth: "none", height: "100%", minHeight: "100%", flex: "0 0 auto", margin: "0 auto",
              border: "0", borderRadius: "var(--jrni-radius-sm)", background: "#fff",
              opacity: props.loading || props.staleNote ? 0.72 : 1, transition: "opacity 120ms ease",
            }}
          />
        ) : props.loading ? (
          <div role="status" aria-label={i18nT("journeys.customTemplates.loadingPreview", "Loading preview")}
            style={{ width: Math.min(widths[device], 960) + "px", maxWidth: "100%", margin: "0 auto", display: "flex", flexDirection: "column", gap: "14px" }}>
            <Skeleton variant="rect" width="100%" height={56} />
            <Skeleton variant="rect" width="100%" height={180} />
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="55%" />
            <Skeleton variant="rect" width="100%" height={120} />
          </div>
        ) : (
          <div style={{ margin: "auto" }}>
            <InlineEmptyState title={props.emptyTitle} description={props.emptyDescription} />
          </div>
        )}
      </div>
    </div>
  );
}
